import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  CatalogTable,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { createVersionColumn } from './components/catalog/VersionColumn';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { configApiRef, googleAuthApiRef, useApi } from '@backstage/core-plugin-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { ReleaseNoteGeneratorPage } from './components/release-notes/ReleaseNoteGeneratorPage';
import { ReleasePage } from './components/release/ReleasePage';
import { PrepareReleaseNotesPage } from './components/release-notes/PrepareReleaseNotesPage';
import { ReleaseVersionsPage } from './components/release-notes/ReleaseVersionsPage';
import { PreviewEnvPage } from './components/preview-envs/PreviewEnvPage';
import { AiQaPage } from './components/ai-qa/AiQaPage';
import {
  aiQaAccessPermission,
  previewAccessPermission,
  releaseAccessPermission,
  releaseNotesAccessPermission,
} from './permissions';

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => {
      const config = useApi(configApiRef);
      const baseUrl = config.getString('app.baseUrl');
      const isLocal = baseUrl.includes('localhost');

      if (isLocal) {
        return <SignInPage {...props} auto providers={['guest']} />;
      }

      return (
        <SignInPage
          {...props}
          auto
          providers={[
            {
              id: 'google-auth-provider',
              title: 'Google',
              message: 'Sign in with your Bartlett Roofs Google account',
              apiRef: googleAuthApiRef,
            },
          ]}
        />
      );
    },
  },
});

const catalogColumns = [
  CatalogTable.columns.createNameColumn({ defaultKind: 'Component' }),
  CatalogTable.columns.createSpecTypeColumn(),
  CatalogTable.columns.createMetadataDescriptionColumn(),
  createVersionColumn('dev', 'Dev'),
  createVersionColumn('stage', 'Stage'),
  createVersionColumn('prod', 'Prod'),
  CatalogTable.columns.createTagsColumn(),
];

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog" element={<CatalogIndexPage columns={catalogColumns} />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route
      path="/release-notes"
      element={
        <RequirePermission permission={releaseNotesAccessPermission}>
          <ReleaseNoteGeneratorPage />
        </RequirePermission>
      }
    />
    <Route
      path="/release"
      element={
        <RequirePermission permission={releaseAccessPermission}>
          <ReleasePage />
        </RequirePermission>
      }
    />
    <Route
      path="/release-notes/prepare"
      element={
        <RequirePermission permission={releaseNotesAccessPermission}>
          <PrepareReleaseNotesPage />
        </RequirePermission>
      }
    />
    <Route
      path="/release-notes/versions"
      element={
        <RequirePermission permission={releaseNotesAccessPermission}>
          <ReleaseVersionsPage />
        </RequirePermission>
      }
    />
    <Route
      path="/preview-envs"
      element={
        <RequirePermission permission={previewAccessPermission}>
          <PreviewEnvPage />
        </RequirePermission>
      }
    />
    <Route
      path="/ai-qa"
      element={
        <RequirePermission permission={aiQaAccessPermission}>
          <AiQaPage />
        </RequirePermission>
      }
    />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
